import React, { useMemo } from 'react';

import currencyFormatter from 'currency-formatter';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Chip, Stack, Typography } from '@mui/material';

import type { CreditReversalOrder } from '~/services/OrdersService';

import { getCashPayments } from '../utils';

interface Props {
  order: CreditReversalOrder;
}

export default function CashPaymentsInfo({ order }: Props) {
  const cashPayments = useMemo(() => getCashPayments(order.payments), [order.payments]);

  if (cashPayments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No cash payments found for this order.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Cash Payments
      </Typography>
      <Stack spacing={1}>
        {cashPayments.map((payment) => (
          <Box
            key={payment.id_payment}
            sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" fontWeight="medium">
                {(payment.ext_transaction_id as string) ?? '—'}
              </Typography>
              <Chip label={String(payment.type)} size="small" variant="outlined" />
              {payment.psp_name && <Chip label={String(payment.psp_name)} size="small" variant="outlined" />}
              <Typography variant="body2">
                {currencyFormatter.format(payment.amount, { code: payment.currency, precision: 2 })} {payment.currency}
              </Typography>
              {payment.success != null &&
                (payment.success ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <ErrorIcon fontSize="small" color="error" />
                ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
